/**
 * Meridian Watch — navbar widget that surfaces actionable alerts.
 *
 * Polls `/workspaces/{id}/watch` every 2 minutes. Each alert is a
 * row in the dropdown; rows with a `link` are clickable and route
 * straight to the source (model page, MR, dataset, etc.). When
 * there are no alerts the trigger button doesn't render — keeps
 * the navbar clean for healthy workspaces.
 *
 * Visual grammar mirrors NotificationBell — small icon trigger +
 * dropdown panel + click-outside close — so the navbar reads as
 * one consistent system.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, ArrowRight, Info, X } from "lucide-react";
import { useNavigate } from "react-router";

import {
  fetchWatchAlerts,
  type WatchAlert,
  type WatchSeverity,
} from "../../lib/watch";

interface WatchProps {
  workspaceId: string | null;
}

const POLL_MS = 120_000;

export function Watch({ workspaceId }: WatchProps) {
  const [alerts, setAlerts] = useState<WatchAlert[]>([]);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const resp = await fetchWatchAlerts(workspaceId);
      setAlerts(resp.alerts);
    } catch {
      // Silent — Watch is best-effort, never blocks the user.
    }
  }, [workspaceId]);

  // Initial fetch + periodic poll. We don't speed up while open
  // (unlike the bell) because Watch alerts change on the order of
  // hours, not seconds.
  useEffect(() => {
    if (!workspaceId) return;
    void refresh();
    const id = window.setInterval(() => void refresh(), POLL_MS);
    return () => window.clearInterval(id);
  }, [workspaceId, refresh]);

  // Click-outside-to-close — same UX as bell + user menu.
  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  // No alerts = no widget. Keeps the navbar empty for healthy
  // workspaces — a Watch icon that always says "all good" trains
  // users to ignore it.
  if (alerts.length === 0) return null;

  const warningCount = alerts.filter((a) => a.severity === "warning").length;
  const tone = warningCount > 0 ? "amber" : "slate";

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`${alerts.length} watch alert${alerts.length === 1 ? "" : "s"}`}
        className={[
          "relative flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 bg-white shadow-sm transition-colors",
          tone === "amber"
            ? "text-amber-600 hover:bg-amber-50"
            : "text-slate-500 hover:bg-slate-100",
        ].join(" ")}
      >
        <AlertTriangle className="h-4 w-4" />
        <span
          aria-hidden
          className={[
            "absolute -right-0.5 -top-0.5 inline-flex min-w-[16px] items-center justify-center rounded-full px-1 text-[10px] font-semibold text-white",
            tone === "amber" ? "bg-amber-500" : "bg-slate-400",
          ].join(" ")}
        >
          {alerts.length > 9 ? "9+" : alerts.length}
        </span>
      </button>

      {open && (
        <div className="absolute right-0 top-11 z-30 w-[400px] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
          <div className="flex items-center justify-between border-b border-slate-100 px-3.5 py-2.5">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
              <span className="text-[13px] font-medium text-slate-900">
                Meridian Watch
              </span>
              <span className="text-[10.5px] text-slate-400">
                {alerts.length} item{alerts.length === 1 ? "" : "s"}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="flex h-6 w-6 items-center justify-center rounded text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
            >
              <X className="h-3 w-3" />
            </button>
          </div>

          <ul className="max-h-[440px] overflow-y-auto">
            {alerts.map((alert) => (
              <AlertRow
                key={alert.id}
                alert={alert}
                onClick={() => {
                  if (alert.link) {
                    navigate(alert.link);
                    setOpen(false);
                  }
                }}
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function AlertRow({
  alert,
  onClick,
}: {
  alert: WatchAlert;
  onClick: () => void;
}) {
  const Icon = severityIcon(alert.severity);
  const tone = severityTone(alert.severity);
  const clickable = !!alert.link;

  const inner = (
    <div className="flex w-full items-start gap-2.5 px-3.5 py-2.5 text-left">
      <Icon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${tone}`} />
      <div className="min-w-0 flex-1">
        <p className="text-[12.5px] font-medium leading-snug text-slate-900">
          {alert.title}
        </p>
        <p className="mt-0.5 line-clamp-3 text-[11.5px] leading-snug text-slate-500">
          {alert.detail}
        </p>
      </div>
      {clickable && (
        <ArrowRight className="mt-0.5 h-3 w-3 shrink-0 text-slate-300" />
      )}
    </div>
  );

  return (
    <li className="border-b border-slate-50 last:border-b-0">
      {clickable ? (
        <button
          type="button"
          onClick={onClick}
          className="block w-full transition-colors hover:bg-slate-50"
        >
          {inner}
        </button>
      ) : (
        <div className="block w-full">{inner}</div>
      )}
    </li>
  );
}

function severityIcon(severity: WatchSeverity) {
  return severity === "warning" ? AlertTriangle : Info;
}

function severityTone(severity: WatchSeverity): string {
  return severity === "warning" ? "text-amber-500" : "text-slate-400";
}
